<script setup lang="ts">
import { THEME_SYMBOL, useThemeService } from '../../composables/useTheme'
import { faviconToLinks, mergeFavicon } from '../../theme/utils/favicon'
import { resolveFavicon } from '../../theme/utils/resolveThemeAsset'
import { themeAssetUrls } from '#theme-assets'
import type { Name } from '#theme'
import { computed, provide, toHexColor, useCoreConfig, useHead, useNativeAppColorScheme } from '#imports'

export interface ThemeProviderProps {
    theme?: Name
    /**
     * When true, applies the theme class to `<body>` and injects the theme's
     * favicon `<link>` entries into `<head>`. Mount exactly one global
     * ThemeProvider per app — multiple instances call `useHead` with the
     * same keys and cause the head manager to flicker between their values.
     */
    global?: boolean
    height?: string
    width?: string
}

const props = withDefaults(defineProps<ThemeProviderProps>(), {
    height: 'initial',
    width: 'initial',
})

const { getComputedTheme } = useThemeService()

const _theme = getComputedTheme(() => props.theme)

provide(THEME_SYMBOL, {
    theme: _theme
})

const computedClass = computed(() => {
    return [`theme--${_theme.value.name}`, `${_theme.value.dark ? 'theme--dark' : 'theme--light'}`]
})

if (props.global) {
    const { site, theme: themeConfig } = useCoreConfig()
    // Inlined rather than calling `useThemeFavicon()` because Vue's
    // `inject` doesn't see the current component's own `provide()` —
    // `useTheme()` would return the parent's theme, not `_theme`.
    const faviconLinks = computed(() => faviconToLinks(
        resolveFavicon(mergeFavicon(_theme.value.favicon, site.favicon), themeAssetUrls),
    ))

    const chromeMeta = computed(() => {
        // Only a site that can switch needs the tag to follow the theme. A site on
        // one theme has whatever it put in `app.head` for a reason — luxcasino and
        // crushit pin a brand accent rather than the page background — and taking
        // that over would repaint their address bar.
        if (!themeConfig.colorScheme) return []

        const background = toHexColor(_theme.value.colors.background)

        if (!background) return []

        return [
            { name: 'theme-color', content: background },
            { name: 'apple-mobile-web-app-status-bar-style', content: _theme.value.dark ? 'black' : 'default' },
        ]
    })

    useHead({
        bodyAttrs: {
            class: () => computedClass.value?.join(' ')
        },
        link: faviconLinks,
        meta: chromeMeta,
    })

    useNativeAppColorScheme(_theme)
}
</script>

<template>
    <div
        class="theme-provider"
        :class="props.global ? undefined : computedClass"
        :style="{ height: props.height, width: props.width }"
    >
        <UIDefaultsProvider v-if="_theme.defaults" :defaults="_theme.defaults">
            <slot />
        </UIDefaultsProvider>
        <slot v-else />
    </div>
</template>
